import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, ShoppingCart, Store, User, Phone, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useMall } from "@/context/MallContext";
import { useSettings } from "@/context/SettingsContext";
import { StoreButton } from "@/components/Buttons";
import { mediaUrl } from "@/lib/api";
import { CART_LABEL, SHOP_NAV } from "@/lib/nav";
import { ZoneChip } from "@/components/layout/ZoneChip";

/** 13thav's own chrome: strictly white, dark ink type — a shop's color starts in the store bar below. */
const INK = { primary: "#0B0B0B", on: "#FFFFFF" };

const ROOMS = SHOP_NAV;

const GROUPS = [["Private sites", "own_sites"], ["Pro Shops", "anchors"], ["Storefronts", "storefronts"], ["Starter shops", "kiosks"]];

/** The directory lives on /stores now — one page, one grid. */
function useGoDirectory() {
    const navigate = useNavigate();
    return () => navigate("/directory");
}

/** The Shops menu opens on a click and closes on a click anywhere else, never on a hover. */
function StoresNav() {
    const { floor, enter, solo } = useMall();
    const [open, setOpen] = useState(false);
    const box = useRef(null);
    const navigate = useNavigate();
    const goDirectory = useGoDirectory();
    const { pathname } = useLocation();

    useEffect(() => setOpen(false), [pathname]);
    useEffect(() => {
        if (!open) return undefined;
        const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
        const esc = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", away);
        document.addEventListener("keydown", esc);
        return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); };
    }, [open]);

    const walkIn = (card) => { setOpen(false); enter(card, "header"); navigate(`/stores/${card.slug}`); };

    // On a tenant's own site there is no mall to walk around.
    if (solo) return null;

    return (
        <div ref={box} className="relative hidden lg:block">
            <button type="button" onClick={() => setOpen((v) => !v)} data-testid="nav-stores" aria-expanded={open} aria-haspopup="menu"
                className="inline-flex items-center gap-1.5 rounded-full border border-black/15 bg-black/[0.04] px-4 py-2 text-sm font-bold uppercase tracking-wide text-zinc-900 transition-colors hover:border-black/40 hover:bg-black/[0.07]">
                <Store size={14} /> Shops <ChevronDown size={13} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
            </button>
            {open && (
                <div className="absolute left-0 top-full z-50 max-h-[75vh] w-72 overflow-y-auto rounded-2xl border border-black/10 bg-white shadow-2xl"
                    data-testid="stores-menu">
                    {GROUPS.map(([label, key]) => {
                        const rows = floor?.[key] || [];
                        if (!rows.length) return null;
                        return (
                            <div key={key} className="border-b border-black/8 last:border-0">
                                <p className="px-4 pb-1 pt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">{label}</p>
                                {rows.map((s) => (
                                    <button key={s.slug} onClick={() => walkIn(s)} data-testid={`stores-menu-${s.slug}`}
                                        className="flex w-full items-center gap-2.5 px-4 py-2 text-left transition-colors hover:bg-black/[0.04]">
                                        <span className="h-6 w-1.5 shrink-0 rounded-full" style={{ background: s.theme?.primary || "#2563EB" }} />
                                        <span className="min-w-0">
                                            <span className="block truncate text-sm font-semibold text-zinc-900">{s.name}</span>
                                            <span className="block font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-500">{s.city}</span>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        );
                    })}
                    <button onClick={() => { setOpen(false); goDirectory(); }} data-testid="stores-menu-directory"
                        className="flex w-full items-center gap-2 bg-black/[0.03] px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-600 hover:text-black">
                        The whole directory →
                    </button>
                </div>
            )}
        </div>
    );
}

export default function Header() {
    const { count, setOpen } = useCart();
    const { user } = useAuth();
    const { leave, floor, enter, solo, soloByHost, site } = useMall();
    const settings = useSettings();
    const [mobile, setMobile] = useState(false);
    const [mobileStores, setMobileStores] = useState(false);
    const loc = useLocation();
    const navigate = useNavigate();
    const goDirectory = useGoDirectory();
    const phone = settings?.business?.phone_raw || "+13475733875";

    const mobileWalkIn = (card) => { setMobile(false); enter(card, "header"); navigate(`/stores/${card.slug}`); };

    return (
        <header className="sticky top-0 z-50 border-b border-black/10 bg-white/95 text-zinc-900 backdrop-blur-xl" data-testid="site-header">
            <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 sm:px-6 lg:gap-6">
                {solo && site?.store ? (
                    <Link to="/" className="flex shrink-0 items-center gap-2.5" data-testid="solo-header-store">
                        {site.store.logo ? (
                            <img src={mediaUrl(site.store.logo)} alt=""
                                className="h-9 w-9 rounded-lg border border-black/10 object-contain" />
                        ) : (
                            <span className="flex h-9 w-9 items-center justify-center rounded-lg font-display text-base font-black text-white"
                                style={{ background: site.store.theme?.primary || INK.primary }}>
                                {site.store.name.slice(0, 1)}
                            </span>
                        )}
                        <span className="max-w-[13rem] truncate font-display text-base font-extrabold tracking-tight text-zinc-900">
                            {site.store.name}
                        </span>
                    </Link>
                ) : (
                    <Link to="/whitelabel" onClick={leave} className="group flex shrink-0 items-center gap-2" data-testid="logo-link" title="13thav — white-label print shop websites">
                        <span className="flex h-9 w-9 items-center justify-center rounded-lg font-display text-lg font-black transition-transform duration-200 group-hover:rotate-6"
                            style={{ background: INK.primary, color: INK.on }} data-testid="logo-mark">13</span>
                        <span className="hidden font-display text-[17px] font-extrabold tracking-tight text-zinc-950 sm:inline" data-testid="logo-wordmark">
                            13thav<span style={{ color: "#1E40AF" }}>.</span>
                        </span>
                    </Link>
                )}
                <ZoneChip className="hidden md:inline-flex" />

                <StoresNav />

                <nav className="hidden items-center gap-0.5 lg:flex">
                    {ROOMS.map((n) => {
                        const active = loc.pathname === n.to || loc.pathname.startsWith(`${n.to}/`);
                        return (
                            <Link key={n.to} to={n.to} data-testid={`nav-${n.key}`}
                                className={`whitespace-nowrap rounded-full px-2 py-2 text-[13px] transition-colors duration-200 xl:px-2.5 xl:text-sm ${active ? "font-bold text-black" : "font-medium text-zinc-600 hover:text-black"}`}>
                                {n.label}
                            </Link>
                        );
                    })}
                </nav>

                <div className="ml-auto flex shrink-0 items-center gap-2">
                    <a href={`tel:${phone}`} className="hidden items-center gap-2 rounded-full border border-black/15 px-3 py-2 text-xs font-semibold text-zinc-700 transition-colors hover:border-black/40 hover:text-black xl:flex" data-testid="header-phone">
                        <Phone size={14} /> (347) 573-3875
                    </a>
                    <Link to={user ? "/account" : "/login"} className="flex h-10 w-10 items-center justify-center rounded-full border border-black/15 text-zinc-700 transition-colors hover:border-black/40 hover:text-black" data-testid="account-link">
                        <User size={18} />
                    </Link>
                    <button onClick={() => setOpen(true)} className="relative flex h-10 w-10 items-center justify-center rounded-full border border-black/15 text-zinc-700 transition-colors hover:border-black/40 hover:text-black" data-testid="cart-button">
                        <ShoppingCart size={18} />
                        {count > 0 && (
                            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-bold"
                                style={{ background: INK.primary, color: INK.on }} data-testid="cart-count">{count}</span>
                        )}
                    </button>
                    <Link to="/cart" className="hidden sm:block" data-testid="header-cart-link">
                        <StoreButton theme={INK} className="px-4 py-2.5" data-testid="header-order-cta"><ShoppingCart size={15} /> {CART_LABEL}</StoreButton>
                    </Link>
                    <button onClick={() => setMobile(true)} className="flex h-10 w-10 items-center justify-center rounded-full border border-black/15 text-zinc-900 lg:hidden" data-testid="mobile-menu-button">
                        <Menu size={20} />
                    </button>
                </div>
            </div>

            {mobile && (
                <div className="fixed inset-0 z-50 text-zinc-900 lg:hidden" data-testid="mobile-menu">
                    <div className="absolute inset-0 bg-zinc-900/40" onClick={() => setMobile(false)} />
                    <div className="absolute right-0 top-0 h-full w-80 overflow-y-auto border-l border-black/10 bg-white p-6 animate-slide-in-right">
                        <div className="mb-6 flex items-center justify-between">
                            <span className="font-display text-lg font-extrabold text-black">Menu</span>
                            <button onClick={() => setMobile(false)} className="text-zinc-500 hover:text-black" data-testid="mobile-menu-close"><X /></button>
                        </div>

                        <div className="flex items-center gap-2">
                            {!solo && (
                                <>
                                    <button onClick={() => { setMobile(false); goDirectory(); }} data-testid="mobile-nav-stores"
                                        className="flex flex-1 items-center gap-2 rounded-xl border border-black/15 bg-black/[0.04] px-3 py-3 text-base font-bold uppercase tracking-wide text-zinc-900">
                                        <Store size={16} /> Shops
                                    </button>
                                    <button onClick={() => setMobileStores((v) => !v)} data-testid="mobile-stores-toggle"
                                        className="flex h-11 w-11 items-center justify-center rounded-xl border border-black/15 text-zinc-700">
                                        <ChevronDown size={18} className={mobileStores ? "rotate-180" : ""} />
                                    </button>
                                </>
                            )}
                        </div>

                        {mobileStores && !solo && (
                            <div className="mt-2 rounded-xl border border-black/10 bg-black/[0.02] p-2" data-testid="mobile-stores-list">
                                {GROUPS.map(([label, key]) => {
                                    const rows = floor?.[key] || [];
                                    if (!rows.length) return null;
                                    return (
                                        <div key={key} className="mb-1">
                                            <p className="px-2 pb-1 pt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">{label}</p>
                                            {rows.map((s) => (
                                                <button key={s.slug} onClick={() => mobileWalkIn(s)} data-testid={`mobile-store-${s.slug}`}
                                                    className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm text-zinc-800 hover:bg-black/[0.04]">
                                                    <span className="h-5 w-1.5 rounded-full" style={{ background: s.theme?.primary || "#2563EB" }} />
                                                    {s.name}
                                                </button>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <div className="mt-4 flex flex-col gap-1 border-t border-black/10 pt-4">
                            {ROOMS.map((n) => (
                                <Link key={n.to} to={n.to} onClick={() => setMobile(false)} className="rounded-lg px-3 py-3 text-base font-medium text-zinc-800 transition-colors hover:bg-black/[0.04]">
                                    {n.label}
                                </Link>
                            ))}
                            <Link to="/artwork-request" onClick={() => setMobile(false)} className="rounded-lg px-3 py-3 text-base font-medium text-zinc-800 hover:bg-black/[0.04]">Artwork Request</Link>
                            <Link to="/website-design" onClick={() => setMobile(false)} className="rounded-lg px-3 py-3 text-base font-medium text-zinc-800 hover:bg-black/[0.04]">Website Design</Link>
                            <Link to={user ? "/account" : "/login"} onClick={() => setMobile(false)} className="rounded-lg px-3 py-3 text-base font-medium text-zinc-800 hover:bg-black/[0.04]">{user ? "My Account" : "Login"}</Link>
                        </div>

                        <Link to="/cart" onClick={() => setMobile(false)} className="mt-6 block" data-testid="mobile-bags-cta">
                            <StoreButton theme={INK} className="w-full"><ShoppingCart size={16} /> {CART_LABEL}</StoreButton>
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
